import { motion } from 'framer-motion';

const ThreeStepsSection = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">
            はじめての方へ | 迷子にならない3ステップ
          </h2>
          
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
            "何から?"で止まってしまう方へ。吉野には今、草刈りも竹林整備も空き家発掘も焼き鳥屋出店も拠点づくりも、同時に走っています。だから最初は<strong className="text-amber-700">『選ばなくてOK』</strong>。まずこの3ステップで、自分の入口を見つけてください。
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Step 1 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-amber-100 text-amber-700 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0">
                01
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                まず「聴く・見る」
              </h3>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-mic-line text-3xl text-amber-600 w-8 h-8 flex items-center justify-center" aria-hidden="true"></i>
                <h4 className="text-xl font-bold text-gray-800">Yoshino Now Voice</h4>
              </div>
              <p className="text-gray-600 text-base mb-2">(YouTube/Podcast)</p>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              吉野の"今"を音声で。移住者の本音、挑戦者の葛藤、リアルな空気感が伝わります。
            </p>

            <div className="bg-stone-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700 font-medium mb-2">おすすめ:</p>
              <p className="text-sm text-gray-600">
                最新回から1本だけ聴いてみてください。古い回から順番に聴かなくてOK。
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <a
                href="https://www.youtube.com/@YoshinoNowVoice"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-center"
                aria-label="YouTubeで聴く"
              >
                YouTubeで聴く
              </a>
              <a
                href="https://open.spotify.com/show/3TSoXSt857tDf0F7g9PVJs?si=9f56ace6cf624616"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-center"
                aria-label="Podcastで聴く"
              >
                Podcastで聴く
              </a>
            </div>
          </motion.div>

          {/* Step 2 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-amber-100 text-amber-700 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0">
                02
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                次に「参加する・見る」
              </h3>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-calendar-event-line text-3xl text-amber-600 w-8 h-8 flex items-center justify-center" aria-hidden="true"></i>
                <h4 className="text-xl font-bold text-gray-800">今週の活動予定</h4>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              実際に現地に来て、メンバーと会ってみる。
            </p>

            <div className="bg-stone-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700 mb-3 font-medium">
                「手伝えるか分からない」<br />
                「見学だけでもいい?」
              </p>
              <p className="text-sm text-amber-700 font-bold">
                ↑この状態で来てOKです。
              </p>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6 text-sm">
              現地で会うと、「あれ、私もできるかも」が不思議と増えていきます。
            </p>

            <button
              onClick={() => scrollToSection('this-week')}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              aria-label="今週の予定をチェック"
            >
              今週の予定をチェック
            </button>
          </motion.div>

          {/* Step 3 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-amber-100 text-amber-700 w-12 h-12 rounded-full flex items-center justify-center font-black text-xl flex-shrink-0">
                03
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">
                そして「相談する」
              </h3>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-message-3-line text-3xl text-amber-600 w-8 h-8 flex items-center justify-center" aria-hidden="true"></i>
                <h4 className="text-xl font-bold text-gray-800">吉野なんでも相談所</h4>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              何をしたいか決まってなくても相談できる入口です。
            </p>

            <div className="bg-stone-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700 font-medium mb-2">相談内容の例:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>・聴いた/見た。次どうする?</li>
                <li>・未定だけど気になる</li>
                <li>・空き家の相談したい</li>
                <li>・視察を検討している</li>
              </ul>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              返信目安:2-3営業日<br />
              <span className="text-gray-500">(速さより信頼を大切に)</span>
            </p>

            <a
              href="/consultation"
              className="block w-full bg-stone-700 hover:bg-stone-800 text-white px-6 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-center"
              aria-label="相談フォームへ"
            >
              相談フォームへ
            </a>
          </motion.div>
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-xl md:text-2xl text-amber-700 font-bold">
            "未定です"って4回言っても大丈夫。<br className="hidden md:block" />
            未定って、前向きな状態なので。
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ThreeStepsSection;
